import { Col, Form, Row } from 'antd';
import { merge, sum } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IAkcionarVHBody, IHlasovani, ISezhlasu } from 'types/akcionar';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { EHlasovani } from 'enums/akcionarKorpAkce/hlasovani';
import { ENotificationType } from 'enums/notificationType';

interface HlasovaniProps {
    page: number;
    body: IAkcionarVHBody;
    bodyHlasovani: IHlasovani;
    maxPocet: number;
    indexBody: number;
    updateHlasovani: (v: IHlasovani) => void;
    setConfirmContinue: React.Dispatch<React.SetStateAction<boolean[] | undefined>>;
    submitedPocet?: number;
    submiting: boolean;
    setAllowToPut: React.Dispatch<React.SetStateAction<boolean>>;
}

const Hlasovani: React.FC<HlasovaniProps> = ({
    page,
    body,
    bodyHlasovani,
    maxPocet,
    indexBody,
    updateHlasovani,
    submitedPocet,
    setConfirmContinue,
    submiting,
    setAllowToPut
}) => {
    const { NotificationComponent } = useContext(Context);
    const [putHlasovani, setPutHlasovani] = useState<ISezhlasu[]>([
        {
            typhlasu: EHlasovani.NULL,
            pocet: 0
        }
    ]);
    const [prubezneHlasovani, setPrubezneHlasovani] = useState<EHlasovani[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [prubeznyPocet, setPrubeznyPocet] = useState<number[]>([]);
    const typHlasovani = body.hlasovani.map(({ hlasovani }) => hlasovani);
    typHlasovani.unshift(EHlasovani.NULL);
    const [form] = Form.useForm();
    /**
     * Ukladame prubezne hlasovani
     * @param value hodnota Selectu hlasovani
     * @param i index pole
     */
    const updatePrubezneHlasovani = useCallback(
        (value: EHlasovani, i: number) => {
            const newArr = [...prubezneHlasovani];
            newArr[i] = value;
            return setPrubezneHlasovani(pv => {
                return merge(pv, newArr);
            });
        },
        [setPrubezneHlasovani, prubezneHlasovani]
    );

    /**
     * Ukladame prubeznu pocet pro hlasovani
     * @param pocet hodnota Inputu => number
     * @param i index pole
     */
    const updatePocet = (pocet: number, i: number) => {
        const newArr = [...prubeznyPocet];
        newArr[i] = pocet;
        setPrubeznyPocet([...newArr]);
    };

    useEffect(() => {
        let initValue =
            prubezneHlasovani.length && prubeznyPocet.length
                ? prubezneHlasovani.map((e, i) => {
                      return {
                          typhlasu: e,
                          pocet: prubeznyPocet[i]
                      };
                  })
                : bodyHlasovani?.sezhlasu || [];

        let initPocet = prubeznyPocet.length ? [...prubeznyPocet] : initValue.map(e => e.pocet);

        if (
            sum(initPocet) < maxPocet &&
            page < 3 &&
            typHlasovani.length - 1 > initValue.length &&
            !initValue.filter(e => e.typhlasu === EHlasovani.NULL).length &&
            (bodyHlasovani.sezhlasu ? bodyHlasovani.sezhlasu : []).length === initValue.length
        ) {
            initValue = [
                ...initValue,
                {
                    typhlasu: EHlasovani.NULL,
                    pocet: 0
                }
            ];
            initPocet = [...initPocet, 0];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formValues: any = {};
        initValue.length &&
            initValue.map((e, i) => {
                formValues[`select${i}`] = e.typhlasu;
                formValues[e.typhlasu] = initPocet[i];
            });

        form.setFieldsValue(formValues);
        setPrubezneHlasovani(initValue.map(e => e.typhlasu));
        setPutHlasovani(initValue);
        setPrubeznyPocet(initPocet);

        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bodyHlasovani, page]);

    // /kontrola form poli.. ak zadana hodnota nebola zapisana confirmationContinue sa nastavi na TRUE a pri pokracovani
    // /sa objavi popup ktory upozorni uzivatela (nachadza sa v HlasovaniAkcionar)
    useEffect(() => {
        if (submiting) {
            if ((submitedPocet || 0) < sum(prubeznyPocet)) {
                setConfirmContinue(pv => {
                    if (pv) {
                        const newState = [...pv];
                        newState[indexBody] = true;
                        return newState;
                    }
                });
            } else {
                setConfirmContinue(pv => {
                    if (pv) {
                        const newState = [...pv];
                        newState[indexBody] = false;
                        return newState;
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submiting]);

    /**
     * Odesle prubezne hlasovani pres sluzbu PUT hlasovaniAkcionarPut,
     * Odesle data do parentu pro pozdejsi POST
     * @param issrlabl issrlabl pole
     * @param index index pole
     */
    const zapsatHlasovani = (issrlabl: string, index: number) => {
        if (sum(prubeznyPocet) > maxPocet) {
            NotificationComponent({
                type: ENotificationType.ERROR,
                title: 'SRD_PRUVODCE_VH_E_CHYBA_HODNOTY',
                description: 'SRD_PRUVODCE_VH_E_CHYBA_MAX'
            });
            return;
        }
        form.setFieldsValue({
            [prubezneHlasovani[index]]: prubeznyPocet[index],
            VYBERTE: 0
        });

        setAllowToPut(true);
        updateHlasovani({
            issrlabl,
            sezhlasu: [
                ...(bodyHlasovani?.sezhlasu || []).filter(({ typhlasu }) => typhlasu !== EHlasovani.NULL),
                {
                    typhlasu: prubezneHlasovani[index],
                    pocet: prubeznyPocet[index]
                }
            ]
        });
    };

    /**
     * Zruseni hlasovani a update jednotlivych dependenci
     */
    const zrusitHlasovani = (issrlabl: string, index: number) => {
        setPrubeznyPocet(oldPocet => [...oldPocet.filter((_v, i) => i !== index)]);
        setPrubezneHlasovani(oldPrubezne => [...oldPrubezne.filter(v => v !== putHlasovani[index]?.typhlasu)]);
        setPutHlasovani(ps => [...ps.filter((_v, i) => i !== index)]);

        const bodyHlas: IHlasovani = {
            issrlabl: issrlabl,
            sezhlasu: [
                ...(bodyHlasovani?.sezhlasu || [])
                    .filter(f => f.typhlasu !== EHlasovani.NULL)
                    .filter((_, i) => i !== index)
            ]
        };
        updateHlasovani(bodyHlas);
        setAllowToPut(true);
    };

    return (
        <>
            <Row>
                <Col>
                    <Paragraph
                        style={{
                            position: 'relative',
                            top: 6
                        }}
                        text={'AKCIONAR_HLASOVANI_POCET'}
                    />
                </Col>
                <Col offset={12}>
                    <Title
                        level={4}
                        text={
                            page === 3
                                ? `${sum(putHlasovani.map(e => e.pocet))}/${maxPocet}`
                                : isNaN(sum(prubeznyPocet))
                                  ? `0/${maxPocet}`
                                  : `${sum(prubeznyPocet)}/${maxPocet}`
                        }
                    />
                </Col>
            </Row>
            <LoadingWrapper loading={loading}>
                {!loading &&
                    putHlasovani.map(
                        (hlas, i) =>
                            (hlas.typhlasu === 'VYBERTE' && page > 2) || (
                                <Row key={i}>
                                    <Col span={24}>
                                        <FormWrapper
                                            onFinish={() => zapsatHlasovani(body.issrlabl, i)}
                                            layoutPosition="inline"
                                            outForm={form}
                                        >
                                            <Col span={8}>
                                                <SelectComponent
                                                    isFormItem={true}
                                                    name={`select${i}`}
                                                    data={typHlasovani
                                                        .filter(
                                                            v =>
                                                                !putHlasovani
                                                                    .filter(e => e.pocet)
                                                                    .map(e => e.typhlasu)
                                                                    .includes(v) || v === putHlasovani[i].typhlasu
                                                        )
                                                        .map(hlasovani => ({
                                                            value: hlasovani,
                                                            labelcz: hlasovani
                                                        }))}
                                                    placeholder={hlas.typhlasu ? hlas.typhlasu : 'NULL'}
                                                    disabled={
                                                        page === 3 ||
                                                        (bodyHlasovani?.sezhlasu && bodyHlasovani?.sezhlasu.length > i
                                                            ? bodyHlasovani?.sezhlasu[i].typhlasu !== EHlasovani.NULL
                                                            : false)
                                                    }
                                                    onChange={(value: EHlasovani) => {
                                                        updatePrubezneHlasovani(value, i);
                                                    }}
                                                    validateTrigger="onSubmit"
                                                    rules={[
                                                        {
                                                            validateTrigger: 'onSubmit'
                                                        },
                                                        {
                                                            validator(_rule, value: string) {
                                                                try {
                                                                    if (value === 'VYBERTE' || !value)
                                                                        return Promise.reject(
                                                                            <TranslateText>
                                                                                NEVALIDNI_POVINNA_POLOZKA
                                                                            </TranslateText>
                                                                        );
                                                                    return Promise.resolve();
                                                                } catch {
                                                                    return Promise.reject();
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                            <Col offset={1} span={6}>
                                                <InputComponent
                                                    name={hlas.typhlasu}
                                                    disabled={
                                                        page === 3 ||
                                                        (bodyHlasovani?.sezhlasu && bodyHlasovani?.sezhlasu.length > i
                                                            ? bodyHlasovani?.sezhlasu[i].typhlasu !== EHlasovani.NULL
                                                            : false)
                                                    }
                                                    onChange={(pocet: React.ChangeEvent<HTMLInputElement>) => {
                                                        updatePocet(parseInt(pocet.target.value), i);
                                                    }}
                                                    initialValue={hlas.pocet}
                                                    rules={[
                                                        {
                                                            validator(_rule, value: number) {
                                                                try {
                                                                    if (value < 1)
                                                                        return Promise.reject(
                                                                            <TranslateText>
                                                                                NEVALIDNI_POVINNA_POLOZKA
                                                                            </TranslateText>
                                                                        );
                                                                    if (!isNaN(value)) return Promise.resolve();
                                                                    return Promise.reject(
                                                                        <TranslateText>NEVALIDNI_NUM</TranslateText>
                                                                    );
                                                                } catch {
                                                                    return Promise.reject();
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                            <Col offset={1} span={6}>
                                                {page === 3 ||
                                                    (!(bodyHlasovani?.sezhlasu && bodyHlasovani?.sezhlasu.length > i
                                                        ? bodyHlasovani?.sezhlasu[i].typhlasu !== EHlasovani.NULL
                                                        : false) && (
                                                        <ButtonComponent
                                                            onClick={() => form.submit()}
                                                            buttontext="ZAPSAT"
                                                            id={`hlasovaniZapsatBtn${i}`}
                                                        />
                                                    ))}
                                                {page === 3 ||
                                                    ((bodyHlasovani?.sezhlasu && bodyHlasovani?.sezhlasu.length > i
                                                        ? bodyHlasovani?.sezhlasu[i].typhlasu !== EHlasovani.NULL
                                                        : false) && (
                                                        <ButtonComponent
                                                            buttontext="ZRUSIT"
                                                            id={`hlasovaniZrusitBtn${i}`}
                                                            onClick={() => {
                                                                zrusitHlasovani(body.issrlabl, i);
                                                                setLoading(true);
                                                            }}
                                                        />
                                                    ))}
                                            </Col>
                                        </FormWrapper>
                                    </Col>
                                </Row>
                            )
                    )}
            </LoadingWrapper>
        </>
    );
};

export default Hlasovani;
